import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Pagination from "../../components/Pagination";
import Avatar from "../../components/Avatar";
import InputSelect from "../../components/InputSelect";
import InputDate from "../../components/InputDate";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";

import "./styles.scss";

const typeLabels = {
    purchase: 'Compra de créditos',
    bonus: 'Bônus',
    plan_grant: 'Créditos do plano',
    manual: 'Lançamento manual',
    consumption: 'Consumo',
    refund: 'Estorno',
    expiration: 'Créditos vencidos'
};

const typeOptions = [
    { value: '', label: 'Todos os tipos' },
    ...Object.entries(typeLabels).map(([value, label]) => ({ value, label }))
];

/**
 * Extrato de créditos (BDM-1057). Estado do filtro na URL, como nas demais
 * listas do sistema: recarregar a página ou voltar de um link mantém a busca.
 */
export const CreditStatement = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ data: [], last_page: 1, current_page: 1, total: 0 });
    const [features, setFeatures] = useState([]);

    const page = parseInt(searchParams.get('page') ?? '1');
    const type = searchParams.get('type') ?? '';
    const feature = searchParams.get('feature') ?? '';
    const start = searchParams.get('start') ?? '';
    const end = searchParams.get('end') ?? '';

    useEffect(() => {
        api.get('/credit').then(res => setFeatures(res.data.resource.features ?? [])).catch(() => {});
    }, []);

    useEffect(() => {
        getTransactions();
    }, [page, type, feature, start, end]);

    const getTransactions = () => {
        setLoading(true);

        api.get('/credit/transaction', { params: { page, type, feature, start, end } })
            .then(res => setData(res.data))
            .catch(renderError)
            .then(() => setLoading(false));
    }

    const setFilter = (key, value) => {
        const params = new URLSearchParams(searchParams);

        if (value) {
            params.set(key, value);
        } else {
            params.delete(key);
        }

        // Trocar um filtro sempre volta para a primeira página: manter a página
        // faria a lista abrir vazia num resultado menor.
        params.delete('page');
        setSearchParams(params);
    }

    const setPage = value => {
        const params = new URLSearchParams(searchParams);

        if (value > 1) {
            params.set('page', value);
        } else {
            params.delete('page');
        }

        setSearchParams(params);
    }

    const exportXls = () => {
        api.get('/credit/transaction/export', { params: { type, feature, start, end }, responseType: 'blob' })
            .then(res => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'extrato-de-creditos.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch(renderError);
    }

    const featureLabel = key => features.find(item => item.key === key)?.label ?? key;

    const describe = transaction => {
        if (transaction.description) return transaction.description;
        if (transaction.feature_key) return featureLabel(transaction.feature_key);

        return typeLabels[transaction.type] ?? transaction.type;
    }

    return (
        <PanelTemplate id="credit-statement">
            <PageHeader
                left={<h1>Extrato de créditos</h1>}
                right={<Button type={`secondary`} size={`small`} svg={`download`} text={`Exportar`} action={exportXls} />}
            />

            <div className="statement-filters">
                <InputSelect
                    value={type}
                    options={typeOptions}
                    change={value => setFilter('type', value)}
                />
                <InputSelect
                    value={feature}
                    options={[{ value: '', label: 'Todos os recursos' }, ...features.map(item => ({ value: item.key, label: item.label }))]}
                    change={value => setFilter('feature', value)}
                />
                <span className="filter-label">Período</span>
                <InputDate value={start} format={`YYYY-MM-DD`} change={value => setFilter('start', value ?? '')} />
                <span className="filter-label">até</span>
                <InputDate value={end} format={`YYYY-MM-DD`} change={value => setFilter('end', value ?? '')} />
            </div>

            <Box className={`no-padding with-pagination`}>
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Quando</th>
                                <th>Lançamento</th>
                                <th>Pessoa</th>
                                <th>Referência</th>
                                <th className="right">Créditos</th>
                                <th className="right">Saldo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading &&
                                <tr><td colSpan={6} className="no-data">Carregando…</td></tr>
                            }
                            {!loading && data.data.length === 0 &&
                                <tr><td colSpan={6} className="no-data">Nenhum lançamento no período.</td></tr>
                            }
                            {!loading && data.data.map(transaction => (
                                <tr key={transaction.id} className={transaction.status === 'released' ? 'released' : ''}>
                                    <td>{new Date(transaction.created_at).toLocaleString('pt-BR')}</td>
                                    <td>
                                        {describe(transaction)}
                                        <span className="cell-note">
                                            {transaction.status === 'held' && 'reserva em andamento'}
                                            {transaction.status === 'released' && 'devolvido — a operação falhou'}
                                            {transaction.status === 'settled' && transaction.usage_units
                                                ? `${transaction.usage_units.toLocaleString('pt-BR')} unidades de uso`
                                                : ''}
                                        </span>
                                    </td>
                                    <td>
                                        {transaction.actor_name
                                            ? <span className="person"><Avatar name={transaction.actor_name} size={26} />{transaction.actor_name}</span>
                                            : <span className="cell-note">sistema</span>}
                                    </td>
                                    <td className="cell-note">{transaction.reference_id ?? '—'}</td>
                                    <td className={`right ${transaction.status === 'released' ? '' : (transaction.credits < 0 ? 'negative' : 'positive')}`}>
                                        {transaction.status === 'released'
                                            ? '—'
                                            : `${transaction.credits > 0 ? '+' : '−'}${Math.abs(transaction.credits)}`}
                                    </td>
                                    <td className="right">{transaction.balance_after?.toLocaleString('pt-BR')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={data.current_page}
                    lastPage={data.last_page}
                    totalResults={data.total}
                    setCurrentPage={setPage}
                    hasNextPage={data.current_page < data.last_page}
                    hasPrevPage={data.current_page > 1}
                />
            </Box>

            <p className="statement-note">
                O extrato nunca é editado: estorno, cortesia e ajuste entram como lançamento novo.
            </p>
        </PanelTemplate>
    );
}

export default CreditStatement;
